import {
  LayoutDashboard,
  FolderOpen,
  Package,
  Truck,
  ShoppingCart,
  Calculator,
  Settings,
  User,
  LogOut,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NavigationSection } from '../App.tsx';
import { useAuth } from '../hook_au/useAuth.tsx';
import { useCurrentUser } from '../hook_au/useCurrentUser.tsx';
import { ROLE_LABELS } from '../types/auth';

const LOGO_SRC = '/assets/interiorlab-logo.png';

interface SidebarProps {
  currentSection: NavigationSection;
  onSectionChange: (section: NavigationSection) => void;
}

const navigationItems = [
  { key: 'dashboard' as NavigationSection, label: 'Dashboard', icon: LayoutDashboard },
  { key: 'projects' as NavigationSection, label: 'Proyectos', icon: FolderOpen },
  { key: 'items' as NavigationSection, label: 'Items/Muebles', icon: Package },
  { key: 'suppliers' as NavigationSection, label: 'Proveedores', icon: Truck },
  { key: 'orders' as NavigationSection, label: 'Pedidos', icon: ShoppingCart },
  { key: 'finances' as NavigationSection, label: 'Finanzas', icon: Calculator },
  { key: 'settings' as NavigationSection, label: 'Configuración', icon: Settings },
];

export function Sidebar({ currentSection, onSectionChange }: SidebarProps) {
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const { user, role } = useCurrentUser();

  const handleLogout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  const visibleItems = navigationItems.filter((item) => {
    if (role === 'logistica') {
      return item.key === 'orders';
    }
    if (item.key === 'settings') {
      return role === 'admin';
    }
    if (item.key === 'finances') {
      return role === 'admin' || role === 'finanza';
    }
    return true;
  });

  return (
    <div className="sticky top-0 h-screen w-64 shrink-0 border-r border-gray-200 bg-white shadow-sm flex flex-col">
      <div className="p-6  border-gray-200">
        <div className="flex flex-col items-center text-center gap-2">
          <img src={LOGO_SRC} alt="Logo interiørlab" className="h-14 w-auto" />
          <p className="text-sm text-gray-500">Gestión de proyectos</p>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentSection === item.key;

            return (
              <li key={item.key}>
                <button
                  onClick={() => onSectionChange(item.key)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-blue-700" />
          </div>
          <div className="text-sm">
            <p className="text-gray-900 leading-tight">{user?.name ?? 'Usuario'}</p>
            <p className="text-gray-500 leading-tight">
              {role ? ROLE_LABELS[role] ?? role : 'Rol no asignado'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
